import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import type { FellowshipSource } from "../data/source";
import { Button, LinkButton } from "../components/Button";
import { Plate } from "../components/Logo";
import { StaticHead, StaticPage, staticStyles as s } from "../components/StaticPage";
import { useTitle } from "../lib/useTitle";

export type RouterContext = { queryClient: QueryClient; source: FellowshipSource };

/** A missing page: the engraving as a plate, one sentence, the way home. */
function NotFound() {
  useTitle("Not found");
  return (
    <StaticPage centred>
      <Plate size={8} />
      <StaticHead title="This page isn’t here." lead="The link may be old, or the page has moved. Everything else is where you left it." />
      <div className={s.actions}>
        <LinkButton to="/home" variant="primary" icon="home">
          Go home
        </LinkButton>
        <LinkButton to="/library" icon="library">
          Open the library
        </LinkButton>
      </div>
    </StaticPage>
  );
}

function Failed({ reset }: { reset: () => void }) {
  useTitle("Something went wrong");
  return (
    <StaticPage centred>
      <Plate size={8} />
      <StaticHead title="Something went wrong on our side." lead="Try again in a moment. If it keeps happening, write to us and we’ll look into it." />
      <div className={s.actions}>
        <Button variant="primary" onClick={reset}>
          Try again
        </Button>
        <LinkButton to="/contact">Contact us</LinkButton>
      </div>
    </StaticPage>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
  errorComponent: ({ reset }) => <Failed reset={reset} />,
});
